import { useState } from 'react';
import { motion } from 'framer-motion';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

const SUPABASE_FUNCTION_URL = 'https://fkhmjffxubgqdvkfjezr.supabase.co/functions/v1/stripe-payment';
const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://lonetrs.com';

const paymentSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Make a Payment | LONE TRS LLC',
  description:
    'Securely pay your deposit or balance for LONE TRS LLC transportation and relocation services. Enter your custom amount and invoice reference for fast, tracked payment.',
  url: `${SITE_URL}/payment`,
  isPartOf: { '@type': 'WebSite', name: 'LONE TRS LLC', url: SITE_URL },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Payment', item: `${SITE_URL}/payment` },
    ],
  },
};

export default function PaymentPage() {
  const [amount, setAmount] = useState('');
  const [customerRef, setCustomerRef] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/[^0-9.]/g, '');
    const parts = val.split('.');
    if (parts.length > 2) return;
    if (parts[1] && parts[1].length > 2) return;
    setAmount(val);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const numAmount = parseFloat(amount);
    if (!amount || isNaN(numAmount) || numAmount <= 0) {
      setError('Please enter a valid payment amount.');
      return;
    }
    if (numAmount < 1) {
      setError('Minimum payment amount is $1.00.');
      return;
    }
    if (!customerRef.trim()) {
      setError('Customer name or invoice number is required.');
      return;
    }

    setIsLoading(true);

    try {
      const origin = window.location.origin;
      const res = await fetch(SUPABASE_FUNCTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: numAmount,
          customerRef: customerRef.trim(),
          successUrl: `${origin}/payment-success`,
          cancelUrl: `${origin}/payment`,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.url) {
        setError(data.error || 'Something went wrong. Please try again.');
        return;
      }

      window.location.href = data.url;
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const formattedPreview =
    amount && !isNaN(parseFloat(amount)) && parseFloat(amount) > 0
      ? `$${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : null;

  return (
    <div className="min-h-screen bg-dark-900 flex flex-col">
      <title>Make a Payment | Secure Deposit &amp; Balance | LONE TRS LLC</title>
      <meta
        name="description"
        content="Pay your deposit or invoice balance to LONE TRS LLC securely online. Enter your custom amount and invoice reference. Stripe-powered, SSL-encrypted payments accepted."
      />
      <meta
        name="keywords"
        content="pay moving deposit Las Vegas, LONE TRS payment, invoice payment transportation, relocation deposit Nevada, secure moving payment"
      />
      <link rel="canonical" href={`${SITE_URL}/payment`} />
      <meta property="og:title" content="Make a Secure Payment | LONE TRS LLC" />
      <meta
        property="og:description"
        content="Pay your deposit or balance securely online. Custom amounts accepted. SSL-encrypted via Stripe."
      />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`${SITE_URL}/payment`} />
      <meta name="twitter:card" content="summary_large_image" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(paymentSchema) }}
      />

      <Navigation />

      <main className="flex-1 flex flex-col">
        {/* Hero Banner */}
        <section className="relative pt-32 pb-16 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/1594c5bfc71d79246172ec432d2800f3.jpg"
              alt="Payment background"
              className="w-full h-full object-cover object-top opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-dark-900/60 via-dark-900/40 to-dark-900" />
          </div>

          <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2 mb-6">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-secure-payment-line text-electric-cyan text-base"></i>
                </div>
                <span className="font-inter text-white/80 text-xs tracking-widest uppercase">Secure Stripe Payment</span>
              </div>
              <h1 className="font-orbitron text-5xl md:text-6xl font-bold text-white mb-4">
                MAKE A <span className="text-electric-blue">PAYMENT</span>
              </h1>
              <p className="font-inter text-white/60 text-lg max-w-xl mx-auto">
                Pay your deposit or balance securely. Enter your amount and reference below.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Payment Form Section */}
        <section className="py-16 px-6 relative">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-electric-blue/5 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-electric-cyan/5 rounded-full blur-3xl" />
          </div>

          <div className="max-w-2xl mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="bg-white/5 backdrop-blur-xl border border-white/15 rounded-3xl overflow-hidden"
            >
              {/* Card Header */}
              <div className="bg-gradient-to-r from-electric-blue/20 to-electric-cyan/20 border-b border-white/10 px-8 py-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-xl">
                    <i className="ri-bank-card-2-line text-electric-cyan text-xl"></i>
                  </div>
                  <div>
                    <h2 className="font-orbitron text-white font-bold text-lg">Payment Details</h2>
                    <p className="font-inter text-white/50 text-xs mt-0.5">All transactions are encrypted & secure</p>
                  </div>
                  <div className="ml-auto flex items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-shield-check-fill text-green-400 text-base"></i>
                    </div>
                    <span className="font-inter text-green-400 text-xs">SSL Secured</span>
                  </div>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="px-8 py-8 space-y-7">
                {/* Custom Amount */}
                <div>
                  <label className="block font-inter text-white/80 text-sm font-medium mb-2">
                    Payment Amount *
                    <span className="ml-2 text-white/40 text-xs font-normal">(Deposit or Balance)</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 font-orbitron text-electric-cyan font-bold text-lg">$</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={amount}
                      onChange={handleAmountChange}
                      required
                      placeholder="0.00"
                      className="w-full pl-10 pr-4 py-4 bg-white/5 border border-white/20 rounded-xl text-white font-orbitron text-2xl font-bold tracking-wider focus:outline-none focus:border-electric-blue transition-colors duration-300 placeholder:text-white/20 placeholder:font-orbitron"
                    />
                    {formattedPreview && (
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 font-inter text-electric-cyan text-sm font-medium">
                        {formattedPreview} USD
                      </span>
                    )}
                  </div>
                  <p className="mt-1.5 font-inter text-white/35 text-xs">
                    Enter the exact amount you were invoiced or agreed upon with our team.
                  </p>
                </div>

                {/* Customer Name / Invoice Number */}
                <div>
                  <label className="block font-inter text-white/80 text-sm font-medium mb-2">
                    Customer Name or Invoice Number *
                  </label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                      <i className="ri-file-list-3-line text-white/40 text-base"></i>
                    </div>
                    <input
                      type="text"
                      value={customerRef}
                      onChange={e => setCustomerRef(e.target.value)}
                      required
                      placeholder="e.g. John Smith or INV-2026-0042"
                      className="w-full pl-11 pr-4 py-4 bg-white/5 border border-white/20 rounded-xl text-white font-inter text-sm focus:outline-none focus:border-electric-blue transition-colors duration-300 placeholder:text-white/25"
                    />
                  </div>
                  <p className="mt-1.5 font-inter text-white/35 text-xs">
                    This helps us match your payment to the correct job. Use your full name or the invoice number we provided.
                  </p>
                </div>

                {/* Error Message */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-start gap-3 bg-red-500/15 border border-red-500/30 rounded-xl px-4 py-3"
                  >
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                      <i className="ri-error-warning-fill text-red-400 text-base"></i>
                    </div>
                    <p className="font-inter text-red-300 text-sm">{error}</p>
                  </motion.div>
                )}

                {/* Payment Summary */}
                {formattedPreview && customerRef.trim() && (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white/5 border border-white/10 rounded-xl px-5 py-4 space-y-2"
                  >
                    <p className="font-inter text-white/50 text-xs uppercase tracking-widest mb-3">Payment Summary</p>
                    <div className="flex justify-between items-center">
                      <span className="font-inter text-white/60 text-sm">Reference</span>
                      <span className="font-inter text-white text-sm font-medium">{customerRef.trim()}</span>
                    </div>
                    <div className="flex justify-between items-center border-t border-white/10 pt-2 mt-2">
                      <span className="font-inter text-white/60 text-sm">Total Due</span>
                      <span className="font-orbitron text-electric-cyan font-bold text-lg">{formattedPreview}</span>
                    </div>
                  </motion.div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-4 bg-gradient-to-r from-electric-blue to-electric-cyan text-dark-900 font-orbitron font-bold text-base rounded-xl hover:opacity-90 active:scale-[0.99] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center justify-center gap-3"
                >
                  {isLoading ? (
                    <>
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-loader-4-line animate-spin text-xl"></i>
                      </div>
                      <span>REDIRECTING TO STRIPE...</span>
                    </>
                  ) : (
                    <>
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-secure-payment-line text-xl"></i>
                      </div>
                      <span>PAY SECURELY NOW</span>
                    </>
                  )}
                </button>

                <p className="text-center font-inter text-white/30 text-xs">
                  You will be redirected to Stripe&apos;s secure checkout. We never store your card details.
                </p>
              </form>
            </motion.div>

            {/* Trust Badges */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-8 grid grid-cols-3 gap-4"
            >
              {[
                { icon: 'ri-shield-keyhole-line', label: '256-bit SSL Encryption' },
                { icon: 'ri-bank-card-line', label: 'All Major Cards Accepted' },
                { icon: 'ri-lock-password-line', label: 'PCI DSS Compliant' },
              ].map((badge) => (
                <div key={badge.label} className="flex flex-col items-center gap-2 bg-white/5 border border-white/10 rounded-xl py-4 px-3">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className={`${badge.icon} text-electric-cyan text-xl`}></i>
                  </div>
                  <p className="font-inter text-white/50 text-xs text-center leading-snug">{badge.label}</p>
                </div>
              ))}
            </motion.div>

            {/* Help Text */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.45 }}
              className="text-center font-inter text-white/30 text-sm mt-6"
            >
              Questions about your invoice?{' '}
              <a href="tel:+14158885336" className="text-electric-cyan hover:text-white transition-colors duration-200 cursor-pointer">
                Call us at (415) 888-5336
              </a>
            </motion.p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
